import type { AnalyticsContext, Plan, SourceSurface } from "./envelope";

/**
 * The `source_surface` tagging seam (M12 §"source_surface tagging seam").
 *
 * Every event carries a `source_surface` on its envelope. Rather than let
 * call sites scatter raw `sourceSurface: "embed"` literals, they build their
 * `AnalyticsContext` through these named factories so the embed → public_map
 * → web_app growth loop is distinguishable AT SOURCE and can never drift to a
 * typo. This module is pure + client-safe (no Node/DB imports) so both the
 * server tracker and `"use client"` beacons import it.
 */

export const SOURCE_SURFACE = {
  /** The signed-in editor app (the canonical name for `editor`). */
  WEB_APP: "web_app",
  /** Back-compat alias for the M0 scaffold's editor call sites. */
  EDITOR: "editor",
  /** A shared read-only map viewed at its public URL. */
  PUBLIC_MAP: "public_map",
  /** A map rendered inside a third-party host page via the embed iframe. */
  EMBED: "embed",
  /** The public /v1 API surface. */
  API: "api",
  /** Server-derived events (webhook reconciler, background jobs, sweeps). */
  SERVER: "server",
} as const satisfies Record<string, SourceSurface>;

/** Identity + plan facet shared by every context factory. */
export interface SurfaceIdentity {
  anonSessionId?: string;
  userId?: string;
  plan?: Plan;
}

function ctx(surface: SourceSurface, id: SurfaceIdentity): AnalyticsContext {
  return {
    sourceSurface: surface,
    anonSessionId: id.anonSessionId,
    userId: id.userId,
    plan: id.plan,
  };
}

/** Context for events fired from the signed-in editor / web app. */
export function webAppContext(id: SurfaceIdentity): AnalyticsContext {
  return ctx(SOURCE_SURFACE.WEB_APP, id);
}

/** Context for events fired while viewing a public map (viral reach). */
export function publicMapContext(id: SurfaceIdentity): AnalyticsContext {
  return ctx(SOURCE_SURFACE.PUBLIC_MAP, id);
}

/** Context for events fired from inside an embed iframe (the retention hook). */
export function embedContext(id: SurfaceIdentity): AnalyticsContext {
  return ctx(SOURCE_SURFACE.EMBED, id);
}

/** Context for events fired from the public /v1 API surface. */
export function apiContext(id: SurfaceIdentity): AnalyticsContext {
  return ctx(SOURCE_SURFACE.API, id);
}

/** Context for server-derived events (webhook reconciler, sweeps, jobs). */
export function serverContext(id: SurfaceIdentity): AnalyticsContext {
  return ctx(SOURCE_SURFACE.SERVER, id);
}
